/**
 * The Filter menu of a list that is held whole: which groups a view offers, which rows a pick keeps, and
 * how the picks live in the address (`?review=Open&review=Resurfaced`, one parameter per group, repeated).
 */
import { STANDING_LABEL } from "../../kit";
import type { PersonLine, ViewDef } from "./views";
import { DUE_BANDS } from "./views/renewals";
import { OVER_6, UNDER_6 } from "./views/winBack";

export interface FilterGroup {
  key: "standing" | "review" | "kind" | "due" | "window";
  label: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  /** The value a row has in this group. */
  valueOf: (row: PersonLine) => string;
}

/** The picks: values chosen per group. A group with none picked keeps every row. */
export type Picks = Readonly<Partial<Record<FilterGroup["key"], readonly string[]>>>;

const same = (value: string) => ({ value, label: value });

/** The standings in the order a person reads them: in good standing first, then by how far they have gone. */
export const STANDING_ORDER = ["active", "suspended", "lapsed", "non_member"] as const;

/** Every group's address parameter. */
export const PICK_KEYS: ReadonlyArray<FilterGroup["key"]> = ["standing", "review", "kind", "due", "window"];

const STANDING: FilterGroup = {
  key: "standing",
  label: "Standing",
  options: STANDING_ORDER.map((value) => ({ value, label: STANDING_LABEL[value] })),
  valueOf: (row) => row.standing ?? "",
};

/** A person in no review queue (a renewal coming up, a win-back) is not a worked case: they are open. */
const REVIEW: FilterGroup = { key: "review", label: "Review", options: ["Open", "Reviewed", "Resurfaced"].map(same), valueOf: (row) => (row.cells.review && row.cells.review !== "—" ? row.cells.review : "Open") };
const KIND: FilterGroup = { key: "kind", label: "Kind", options: ["Joined", "Rejoined", "Unknown"].map(same), valueOf: (row) => row.cells.kind ?? "" };
const WINDOW: FilterGroup = { key: "window", label: "Window", options: [UNDER_6, OVER_6].map(same), valueOf: (row) => row.cells.window ?? "" };
const DUE: FilterGroup = { key: "due", label: "Due", options: DUE_BANDS.map((b) => same(b.label)), valueOf: (row) => row.cells.due ?? "" };

/**
 * What a view can be filtered by. A paged view has none of these (its facets are the server's). Standing is
 * offered only when the rows differ in it: on a list of one standing it would be four choices for nothing.
 */
export function filterGroups(view: ViewDef, rows: readonly PersonLine[]): FilterGroup[] {
  if (view.paged) return [];
  const has = (key: string) => view.columns.some((c) => c.key === key);
  const mixed = has("standing") && new Set(rows.map((r) => r.standing)).size > 1;
  return [mixed && STANDING, has("review") && REVIEW, view.id === "arrivals" && KIND, view.id === "renewals" && DUE, view.id === "win-back" && WINDOW].filter((g): g is FilterGroup => g !== false);
}

/** A worked list opens on what needs doing: the open and resurfaced cases, the number the sidebar shows. Reviewed ones are one tick away. Every other list opens whole, the number Today shows. */
export function defaultPicks(view: ViewDef): Picks {
  return view.queue ? { review: ["Open", "Resurfaced"] } : {};
}

/** The picks in the address; a group with no parameter keeps the view's default, and an empty one keeps everything. */
export function readPicks(params: URLSearchParams, groups: readonly FilterGroup[], view: ViewDef): Picks {
  const picks: Partial<Record<FilterGroup["key"], readonly string[]>> = { ...defaultPicks(view) };
  for (const g of groups) if (params.has(g.key)) picks[g.key] = params.getAll(g.key).filter(Boolean);
  return picks;
}

/** Write the picks back: a group at its default leaves the address. */
export function writePicks(params: URLSearchParams, groups: readonly FilterGroup[], view: ViewDef, picks: Picks): void {
  const fixed = defaultPicks(view);
  for (const g of groups) {
    params.delete(g.key);
    const now = picks[g.key] ?? [];
    const was = fixed[g.key] ?? [];
    if (now.length === was.length && now.every((v) => was.includes(v))) continue;
    if (now.length === 0) params.set(g.key, "");
    else for (const v of now) params.append(g.key, v);
  }
  params.delete("page");
}

export function toggle(picks: Picks, key: FilterGroup["key"], value: string): Picks {
  const now = picks[key] ?? [];
  return { ...picks, [key]: now.includes(value) ? now.filter((v) => v !== value) : [...now, value] };
}

const sameValues = (a: readonly string[] = [], b: readonly string[] = []) => a.length === b.length && a.every((v) => b.includes(v));

/** How many groups are set to something other than how the view opens: a default is not a filter in use, so it does not light the badge. */
export function pickCount(view: ViewDef, picks: Picks): number {
  const fixed = defaultPicks(view);
  const keys = new Set([...Object.keys(fixed), ...Object.keys(picks)] as Array<FilterGroup["key"]>);
  return [...keys].filter((k) => !sameValues(picks[k], fixed[k])).length;
}

/** Whether the picks are the ones the view opens with. */
export const isDefault = (view: ViewDef, picks: Picks): boolean => pickCount(view, picks) === 0;

/** The rows a pick keeps. `keep` is the person whose sheet is open: they stay, so working a queue does not pull the row from under the sheet. */
export function applyPicks(rows: PersonLine[], groups: FilterGroup[], picks: Picks, keep: number | null = null): PersonLine[] {
  const active = groups.filter((g) => (picks[g.key]?.length ?? 0) > 0);
  return active.length === 0 ? rows : rows.filter((row) => row.id === keep || active.every((g) => picks[g.key]?.includes(g.valueOf(row))));
}
